/**
 * v0.3 データ公開ゲート API呼出 (S-7)
 * 既存コードベースには per-feature hook file の慣例が無いため (apiFetchをページ内で直接呼ぶ方式)、
 * ここでは薄い関数群のみ定義し、呼び出し元コンポーネントで useQuery/useMutation に渡す。
 */
import { apiFetch } from '@/lib/api';
import type { PublishStatusResponse, PublishDetailResponse } from '@/lib/types-publish-gate';

export const fetchPublishStatus = (params: {
  year_month?: string;
  year_month_from?: string;
  year_month_to?: string;
  node_id?: string;
}): Promise<PublishStatusResponse> => {
  const qs = new URLSearchParams();
  if (params.year_month) qs.set('year_month', params.year_month);
  if (params.year_month_from) qs.set('year_month_from', params.year_month_from);
  if (params.year_month_to) qs.set('year_month_to', params.year_month_to);
  if (params.node_id) qs.set('node_id', params.node_id);
  const q = qs.toString();
  return apiFetch<PublishStatusResponse>(`/publish/status${q ? `?${q}` : ''}`);
};

export const fetchPublishDetail = (nodeId: string, targetMonth: string): Promise<PublishDetailResponse> =>
  apiFetch<PublishDetailResponse>(`/publish/status/${nodeId}/${targetMonth}/detail`);

export const postInputReady = (body: {
  node_id: string; target_month: string; data_type: string; input_ready: boolean;
}) => apiFetch('/publish/input-ready', { method: 'POST', body: JSON.stringify(body) });

// v0.4 章⑤ (D-15/D-23/D-79): status を追加 (既定 'published'、v0.3までの挙動を維持)
export const postPublish = (body: { node_id: string; target_month: string; allow_partial: boolean; status?: 'preliminary' | 'published' }) =>
  apiFetch<{ publish_status_id: string; status: 'preliminary' | 'published'; partial_publish: boolean; missing_data_types: string[] }>(
    '/publish/publish', { method: 'POST', body: JSON.stringify(body) },
  );

export const postUnpublish = (body: { node_id: string; target_month: string }) =>
  apiFetch<{ publish_status_id: string; status: 'draft' }>('/publish/unpublish', { method: 'POST', body: JSON.stringify(body) });

export const postMarkNotApplicable = (body: {
  node_id: string; data_type: string; scope: 'permanent' | 'spot'; target_month?: string; note?: string;
}) => apiFetch('/publish/mark-not-applicable', { method: 'POST', body: JSON.stringify(body) });

export const deleteMarkNotApplicable = (declarationId: string) =>
  apiFetch(`/publish/mark-not-applicable/${declarationId}`, { method: 'DELETE' });

// v0.5⑤ (ダッシュボード集約 Phase 1、Full版AIコメント手動生成、docs/design/v05-dashboard-consolidation-detail.md §3):
// レスポンス形は POST /lite-dashboard/comment 相当 (snake_case) で PublishDetailResponse とは別形。
// 呼び出し元は結果を直接使わず refetch() で正典の GET /publish/status/:nodeId/:month/detail を
// 引き直す方針にする (§8-64型の「2つの形が食い違う」を避ける)。
// ⚠️ platform-reviewer 2巡目CRITICAL: targetMonth は 'YYYY-MM' (7文字) のまま渡すこと。バックエンドは
// publish-gate系関数と同じ7文字を要求する (末尾に '-01' を付けると常に「未公開」扱いになる恒久バグだった)。
export const postGenerateComment = (nodeId: string, targetMonth: string) =>
  apiFetch(`/dashboard/nodes/${nodeId}/comment`, {
    method: 'POST',
    body: JSON.stringify({ month: targetMonth }),
  });
